import { useEffect, useState } from 'react';
import { Box, Grid, Typography, Button, Select, MenuItem, Checkbox, FormControlLabel, FormControl, InputLabel } from '@mui/material';
import { useParams } from 'react-router-dom';
import useApi from 'hooks/useApi';

/**
 * "Dynamic Field" configuration block for a form field.
 *
 * A dynamic field takes its value/choices from answers recorded earlier in the
 * same transaction instead of a hardcoded list. The mapping is stored on the
 * field model as:
 *
 *   settings.isDynamicField = true
 *   settings.dynamicFields  = [{ step: <stepUid>, field: <fieldUid> }, ...]
 *
 * Steps and their fields come from the same source the rule engine uses.
 */

interface DynamicFieldSettingsProps {
  values: any;
  index: number;
  setFieldValue: (key: string, value: any) => void;
  /** Settings popover open state — steps are fetched lazily on first open. */
  open: boolean;
  /** Optional hint shown under the checkbox, e.g. what a source field must be. */
  helperText?: string;
}

export default function DynamicFieldSettings({ values, index, setFieldValue, open, helperText }: DynamicFieldSettingsProps) {
  const { teamUid } = useParams<{ teamUid: string }>();
  const { getFlowByTeamUid } = useApi();
  const [flowSteps, setFlowSteps] = useState<any[]>([]);

  useEffect(() => {
    if (!open || !teamUid || flowSteps.length > 0) {
      return;
    }
    let active = true;
    (async () => {
      try {
        const response = await getFlowByTeamUid(teamUid);
        if (active && response?.success && response?.data?.steps) {
          setFlowSteps(response.data.steps);
        }
      } catch (error) {
        console.error('Failed to fetch flow steps for dynamic field:', error);
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, teamUid]);

  const settings = values.fields[index]?.settings || {};
  const isDynamicField = settings.isDynamicField || false;
  const dynamicFields: { step: string; field: string }[] =
    Array.isArray(settings.dynamicFields) && settings.dynamicFields.length > 0
      ? settings.dynamicFields
      : [{ step: '', field: '' }];

  const getStepFields = (stepUid: string) => flowSteps.find((step: any) => step.uid === stepUid)?.component?.fields || [];

  const updateDynamicFields = (next: { step: string; field: string }[]) =>
    setFieldValue(`fields.${index}.settings.dynamicFields`, next);

  const handleDynamicStepChange = (rowIndex: number, stepUid: string) =>
    updateDynamicFields(dynamicFields.map((row, i) => (i === rowIndex ? { step: stepUid, field: '' } : row)));

  const handleDynamicFieldChange = (rowIndex: number, fieldUid: string) =>
    updateDynamicFields(dynamicFields.map((row, i) => (i === rowIndex ? { ...row, field: fieldUid } : row)));

  const addDynamicRow = () => updateDynamicFields([...dynamicFields, { step: '', field: '' }]);

  const removeDynamicRow = (rowIndex: number) => updateDynamicFields(dynamicFields.filter((_, i) => i !== rowIndex));

  return (
    <>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isDynamicField}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setFieldValue(`fields.${index}.settings.isDynamicField`, isChecked);
                if (!isChecked) {
                  setFieldValue(`fields.${index}.settings.dynamicFields`, []);
                } else if (!Array.isArray(settings.dynamicFields) || settings.dynamicFields.length === 0) {
                  setFieldValue(`fields.${index}.settings.dynamicFields`, [{ step: '', field: '' }]);
                }
              }}
            />
          }
          label="Dynamic Field"
        />
        {helperText && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1, ml: 4 }}>
            {helperText}
             </Typography>
        )}
      </Grid>

      {isDynamicField && (
        <>
          {dynamicFields.map((row, rowIndex) => {
            const rowStepFields = getStepFields(row.step);
            return (
              <Grid item xs={12} key={rowIndex}>
                <Box sx={{ border: '1px solid #eee', borderRadius: 1, p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                      Mapping {rowIndex + 1}
                    </Typography>
                    {dynamicFields.length > 1 && (
                      <Button size="small" color="error" onClick={() => removeDynamicRow(rowIndex)}>
                        Remove
                      </Button>
                    )}
                  </Box>

                  <FormControl fullWidth size="small">
                    <InputLabel>Step</InputLabel>
                    <Select label="Step" value={row.step || ''} onChange={(e) => handleDynamicStepChange(rowIndex, e.target.value)}>
                      {flowSteps.map((step: any) => (
                        <MenuItem key={step.uid} value={step.uid}>
                          {step.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small" disabled={!row.step}>
                    <InputLabel>Field</InputLabel>
                    <Select label="Field" value={row.field || ''} onChange={(e) => handleDynamicFieldChange(rowIndex, e.target.value)}>
                      {rowStepFields.map((f: any, fieldIndex: number) => (
                        <MenuItem key={f.uid || fieldIndex} value={f.uid || f.question}>
                          {f.question}
                        </MenuItem>
                      ))}
                      {rowStepFields.length === 0 && (
                        <MenuItem disabled>{row.step ? 'No fields in this step' : 'Select a step first'}</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
            );
          })}

          <Grid item xs={12}>
            <Button size="small" variant="outlined" onClick={addDynamicRow}>
              Add more
            </Button>
          </Grid>
        </>
      )}
    </>
  );
}
